import { PermissionDto } from '@/api/generated';

import { RouteEnum } from './Route.model';

const CONTACT_EDIT: PermissionDto[] = [PermissionDto.ContactEdit, PermissionDto.ContactManageAll];

/**
 * Permission codes (from the backend catalog) guarding each route. A route opens for a user holding
 * at least one of the listed permissions; an empty list means the route is open to every signed-in
 * user. A list route requires the READ permission of its area, so the left navigation shows a module
 * exactly when its area can be read. Modules without an own catalog area borrow one (see CONTEXT.md):
 * offer and component definitions use the dictionary area, failed workflow actions the offer area and
 * suppliers the supervisor area. An offer inside a process follows the offer area, which the backend
 * enforces on reading it. A screen that also reads another area lists that area's permission in
 * ROUTES_ALSO_REQUIRED_PERMISSIONS.
 */
export const ROUTES_PERMISSIONS: Record<RouteEnum, PermissionDto[]> = {
  [RouteEnum.CARS]: [PermissionDto.CarRead],
  [RouteEnum.CARS_DETAILS]: [PermissionDto.CarRead],
  [RouteEnum.CHATS]: [],
  [RouteEnum.COMMISSION_ADMINISTRATION]: [PermissionDto.CommissionReadAll],
  [RouteEnum.COMMISSION_CONFIGURATION]: [PermissionDto.CommissionManageConfiguration],
  [RouteEnum.COMMISSIONS]: [PermissionDto.CommissionRead],
  [RouteEnum.COMPONENT_DEFINITION_DETAILS]: [PermissionDto.DictionaryRead],
  [RouteEnum.COMPONENT_DEFINITIONS]: [PermissionDto.DictionaryRead],
  [RouteEnum.CONTACTS]: [PermissionDto.ContactRead],
  [RouteEnum.CONTACTS_CREATE]: [PermissionDto.ContactCreate],
  [RouteEnum.CONTACTS_DETAILS]: [PermissionDto.ContactRead],
  [RouteEnum.CONTACTS_EDIT]: CONTACT_EDIT,
  [RouteEnum.CUSTOMERS]: [PermissionDto.ContactRead],
  [RouteEnum.CUSTOMERS_CREATE_BUSINESS]: [PermissionDto.ContactCreate],
  [RouteEnum.CUSTOMERS_CREATE_FOREIGN]: [PermissionDto.ContactCreate],
  [RouteEnum.CUSTOMERS_CREATE_INDIVIDUAL]: [PermissionDto.ContactCreate],
  [RouteEnum.CUSTOMERS_DETAILS]: [PermissionDto.ContactRead],
  [RouteEnum.CUSTOMERS_EDIT]: CONTACT_EDIT,
  [RouteEnum.DASHBOARD]: [],
  [RouteEnum.DICTIONARIES]: [PermissionDto.DictionaryRead],
  [RouteEnum.EMAILS]: [],
  [RouteEnum.EMERGENCY_CONTACTS]: [PermissionDto.EmergencyContactRead],
  [RouteEnum.EMERGENCY_CONTACTS_CREATE]: [PermissionDto.EmergencyContactCreate],
  [RouteEnum.EMERGENCY_CONTACTS_DETAILS]: [PermissionDto.EmergencyContactRead],
  [RouteEnum.EMERGENCY_CONTACTS_EDIT]: [PermissionDto.EmergencyContactEdit],
  [RouteEnum.FAILED_WORKFLOW_ACTIONS]: [PermissionDto.OfferRead],
  [RouteEnum.FORBIDDEN]: [],
  [RouteEnum.LOGIN_SUCCESSFUL]: [],
  [RouteEnum.NOT_AUTHORIZED]: [],
  [RouteEnum.NOT_FOUND]: [],
  [RouteEnum.OFFER_DEFINITION_CREATE]: [PermissionDto.DictionaryCreate],
  [RouteEnum.OFFER_DEFINITION_DETAILS]: [PermissionDto.DictionaryRead],
  [RouteEnum.OFFER_DEFINITIONS]: [PermissionDto.DictionaryRead],
  [RouteEnum.PROCESSES]: [PermissionDto.ProcessRead],
  [RouteEnum.PROCESSES_DETAILS]: [PermissionDto.ProcessRead],
  [RouteEnum.PROCESSES_DETAILS_OFFER_INSTANCE]: [PermissionDto.OfferRead],
  [RouteEnum.RECURRING_RESERVATION_DETAILS]: [PermissionDto.RecurringReservationRead],
  [RouteEnum.RECURRING_RESERVATIONS]: [PermissionDto.RecurringReservationRead],
  [RouteEnum.REPORTS]: [PermissionDto.ReportsRead],
  [RouteEnum.RESOURCE_CALENDAR]: [PermissionDto.ProcessRead],
  [RouteEnum.ROLES]: [PermissionDto.RoleRead],
  [RouteEnum.ROOT]: [],
  [RouteEnum.SUPERVISORS]: [PermissionDto.SupervisorRead],
  [RouteEnum.SUPPLIERS]: [PermissionDto.SupervisorRead],
  [RouteEnum.SUPPLIERS_DETAILS]: [PermissionDto.SupervisorRead],
  [RouteEnum.TASK_DETAILS]: [PermissionDto.TaskRead, PermissionDto.TaskReadAll],
  [RouteEnum.TASKS]: [PermissionDto.TaskRead, PermissionDto.TaskReadAll],
  [RouteEnum.USERS]: [PermissionDto.UserRead],
  [RouteEnum.WAITING_FOR_APPROVAL]: [],
};

/**
 * Permissions a route requires on top of ROUTES_PERMISSIONS, every one of them: screens that cannot
 * work without reading another area. The offer and the resource calendar read dictionary entries,
 * their calendar configurations and availability, which the backend opens with dictionary read; the
 * offer screen also reads its process, which needs process read. Rezerwacje stałe pick their resource
 * among dictionary entries of the types with calendars, read with dictionary read.
 */
export const ROUTES_ALSO_REQUIRED_PERMISSIONS: Partial<Record<RouteEnum, PermissionDto[]>> = {
  [RouteEnum.PROCESSES_DETAILS_OFFER_INSTANCE]: [
    PermissionDto.DictionaryRead,
    PermissionDto.ProcessRead,
  ],
  [RouteEnum.RECURRING_RESERVATION_DETAILS]: [PermissionDto.DictionaryRead],
  [RouteEnum.RECURRING_RESERVATIONS]: [PermissionDto.DictionaryRead],
  [RouteEnum.RESOURCE_CALENDAR]: [PermissionDto.DictionaryRead],
};
